import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useOnclickOutside from 'react-cool-onclickoutside';
import { useSpring, animated, config } from 'react-spring';
import ContributorsFilterContext, { reducer, defaultState } from './contributors-components/context';
import {
  DropdownContainer,
  FilterContainer,
  FilterLabel,
} from './common/styles';
import DropdownLabel from './contributors-components/dropdown-label';
import DropdownArrow from './common/dropdown-arrow';
import Title from './contributors-components/title';
import Actions from './contributors-components/actions';
import ContributorOption from './contributors-components/contributor-option';
import StackedAvatars from './contributors-components/stacked-avatars';

const SDropdownContainer = styled(DropdownContainer)`
  width: 190px;
  padding-left: 16px;
`;

const WarnableContainer = styled(animated.div)`
  width: 334px;
  padding: 16px;
  border: 1px solid #d7dfe7;
  border-radius: 4px;
  position: absolute;
  margin-top: 6px;
  right: 0;
  background: #FFFFFF;
  display: flex;
  flex-direction: column;
  z-index: 2;
`;

const stringifySelected = array => array
  .filter(c => c.selected)
  .map(c => c.id)
  .join(',');

export default function Contributors({ contributors, onChange }) {
  const [state, dispatch] = React.useReducer(reducer, defaultState);
  const [expanded, setExpanded] = React.useState(false);
  const [warningTid, setWarningTid] = React.useState(null);
  const spring = useSpring({
    borderColor: state.warning ? '#b71e49' : '#d7dfe7',
    config: config.molasses,
  });

  const warn = () => {
    clearTimeout(warningTid);
    dispatch({ type: 'setWarning', payload: true });
    const tid = setTimeout(() => dispatch({ type: 'setWarning', payload: false }), 2000);
    setWarningTid(tid);
  };

  const ref = useOnclickOutside(() => {
    if (state.noneSelected) {
      warn();
      return;
    }
    // We have a vast area outside of this component. Any elements on the page
    // can trigger this hook's callback.
    // Trying to minimize the chance the actual contributors needs to be set.
    if (stringifySelected(contributors) !== stringifySelected(state.selection)) {
      onChange(state.selection);
    }
    setExpanded(false);
  });

  React.useEffect(() => {
    dispatch({ type: 'setSelection', payload: contributors });
  }, [contributors]);

  const toggleShowOptions = e => {
    e && e.preventDefault();
    if (expanded && state.noneSelected) {
      warn();
      return;
    }
    setExpanded(!expanded);
  };

  const selected = state.selection.filter(c => c.selected);

  return (
    <ContributorsFilterContext.Provider value={{ state, dispatch }}>
      <FilterContainer ref={ref}>
        <FilterLabel>Contributors</FilterLabel>
        <SDropdownContainer onClick={toggleShowOptions}>
          <DropdownLabel contributors={state.selection} />
          <DropdownArrow expanded={expanded} />
        </SDropdownContainer>
        {expanded && (
          <WarnableContainer style={spring}>
            <Title />
            <Actions />
            {state.selection.map(c => <ContributorOption {...c} key={`contributor-${c.id}`} />)}
          </WarnableContainer>
        )}
        {!state.allSelected && <StackedAvatars contributors={selected} />}
      </FilterContainer>
    </ContributorsFilterContext.Provider>
  );
}

Contributors.propTypes = {
  contributors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    profilePictureUrl: PropTypes.string,
    selected: PropTypes.bool.isRequired,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
};
